import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Calendar,
  Award,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getTierBadgeClass = (tier) => {
  switch (tier) {
    case "bronze": return "bg-amber-900/50 text-amber-300 border-amber-700";
    case "silver": return "bg-slate-600/50 text-slate-200 border-slate-500";
    case "gold": return "bg-yellow-900/50 text-yellow-300 border-yellow-700";
    case "platinum": return "bg-purple-900/50 text-purple-300 border-purple-700";
    default: return "bg-slate-700 text-slate-300 border-slate-600";
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case "active":
      return <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
    case "pending":
      return <Badge className="bg-amber-900/50 text-amber-300 border-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>;
    case "churned":
      return <Badge className="bg-red-900/50 text-red-300 border-red-700"><XCircle className="w-3 h-3 mr-1" />Churned</Badge>;
    default:
      return <Badge className="bg-slate-700 text-slate-300">{status}</Badge>;
  }
};

const getPlanBadge = (plan) => {
  switch (plan) {
    case "basic": return <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">Basic</Badge>;
    case "premium": return <Badge className="bg-purple-900/50 text-purple-300 border-purple-700">Premium</Badge>;
    case "brokerage": return <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-700">Brokerage</Badge>;
    default: return <Badge className="bg-slate-700 text-slate-300">{plan || "Unknown"}</Badge>;
  }
};

const AdminPartnerDetail = () => {
  const { partnerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const response = await axios.get(`${API}/admin/partners/${partnerId}`, { withCredentials: true });
        setData(response.data);
      } catch (error) {
        toast.error("Failed to load partner details");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPartner();
  }, [partnerId]);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64 bg-slate-700" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 bg-slate-700" />
          <Skeleton className="h-64 bg-slate-700 lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-slate-400">Partner not found</p>
      </div>
    );
  }

  const { partner, referrals, recent_referrals, earnings, commissions } = data;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/partners">
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-700">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">{partner.name}</h1>
          <p className="text-slate-400">{partner.email}</p>
        </div>
        <Badge className={`${getTierBadgeClass(partner.tier)} capitalize ml-auto`}>
          {partner.tier} Tier
        </Badge>
      </div>

      {/* Profile & Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Partner Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300">{partner.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Award className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300 capitalize">{partner.partner_type || "Influencer"}</span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300">
                Joined {new Date(partner.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">Partner Code</p>
              <p className="text-lg font-mono text-purple-400">{partner.partner_code}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Commission Rate</p>
              <p className="text-2xl font-bold text-white">{partner.commission_rate}%</p>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <Users className="w-5 h-5 text-blue-400 mb-2" />
              <p className="text-2xl font-bold text-white">{referrals.total}</p>
              <p className="text-xs text-slate-400">Total Referrals</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-2xl font-bold text-emerald-400">{referrals.active}</p>
              <p className="text-xs text-slate-400">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <Clock className="w-5 h-5 text-amber-400 mb-2" />
              <p className="text-2xl font-bold text-amber-400">{referrals.pending}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <XCircle className="w-5 h-5 text-red-400 mb-2" />
              <p className="text-2xl font-bold text-red-400">{referrals.churned}</p>
              <p className="text-xs text-slate-400">Churned</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800 border-slate-700 col-span-2">
            <CardContent className="p-4">
              <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-2xl font-bold text-white">${earnings.all_time?.toLocaleString() || 0}</p>
              <p className="text-xs text-slate-400">All-Time Earnings</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <TrendingUp className="w-5 h-5 text-purple-400 mb-2" />
              <p className="text-2xl font-bold text-purple-400">${earnings.last_30_days?.toLocaleString() || 0}</p>
              <p className="text-xs text-slate-400">Last 30 Days</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <Clock className="w-5 h-5 text-amber-400 mb-2" />
              <p className="text-2xl font-bold text-amber-400">${earnings.pending?.toLocaleString() || 0}</p>
              <p className="text-xs text-slate-400">Pending Payout</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Plan Breakdown */}
      {referrals.by_plan && Object.keys(referrals.by_plan).length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white">Referrals by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              {Object.entries(referrals.by_plan).map(([plan, count]) => (
                <div key={plan} className="flex items-center gap-2">
                  {getPlanBadge(plan)}
                  <span className="text-white font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Referrals */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">Recent Referrals</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Signup Date</TableHead>
                <TableHead className="text-slate-400 text-right">Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent_referrals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    No referrals yet
                  </TableCell>
                </TableRow>
              ) : (
                recent_referrals.map((ref, idx) => (
                  <TableRow key={idx} className="border-slate-700 hover:bg-slate-700/50">
                    <TableCell>
                      <p className="text-white">{ref.referred_name}</p>
                      <p className="text-sm text-slate-400">{ref.referred_email}</p>
                    </TableCell>
                    <TableCell>{getPlanBadge(ref.plan)}</TableCell>
                    <TableCell>{getStatusBadge(ref.status)}</TableCell>
                    <TableCell className="text-slate-300">
                      {new Date(ref.signup_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right text-emerald-400 font-medium">
                      ${ref.monthly_commission?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Commission History */}
      {commissions && commissions.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white">Commission History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Month</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Payment Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.map((comm, idx) => (
                  <TableRow key={idx} className="border-slate-700 hover:bg-slate-700/50">
                    <TableCell className="text-white">{comm.commission_month}</TableCell>
                    <TableCell className="text-emerald-400 font-medium">${comm.amount?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      <Badge className={comm.payment_status === "paid" 
                        ? "bg-emerald-900/50 text-emerald-300 border-emerald-700" 
                        : "bg-amber-900/50 text-amber-300 border-amber-700"
                      }>
                        {comm.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {comm.payment_date ? new Date(comm.payment_date).toLocaleDateString() : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPartnerDetail;
